import React, { useCallback, useMemo } from "react";

import { useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import { SelectField, SelectFieldComponent } from "@mapmycustomers/ui";

import useSourceObjectOptions from "@app/util/hook/useSourceObjectOptions";

import {
  doesSupportValue,
  fromSelectValues,
  STANDARD_OPTION_VALUE,
  toSelectValues,
} from "./values";

export { SOURCE_OBJECT_FILTER_OPERATORS } from "./values";

interface SourceObjectFilterProps extends IFilterComponentProps {}

// No getHumanReadableDescription: the method has no live caller, and the shared helper cannot
// express NONE_OF. Options come from redux through a hook, so doesSupportOption is not needed.
const SourceObjectFilter: IFilterInstance = {
  doesSupportValue,
  getComponent:
    (): ((props: SourceObjectFilterProps) => JSX.Element) =>
    ({ className, disabled, focus, onChange, options, placeholder, value }) => {
      const intl = useIntl();
      const entityType = options?.[FilterOption.ENTITY_TYPE] as EntityType | undefined;
      const sourceObjectOptions = useSourceObjectOptions(entityType);

      const selectOptions = useMemo(
        () =>
          sourceObjectOptions.map((option) => ({
            label: option.label,
            value: option.value ?? STANDARD_OPTION_VALUE,
          })),
        [sourceObjectOptions]
      );

      const setRef = useCallback(
        (ref: null | SelectFieldComponent) => {
          if (focus && ref) {
            ref.focus();
          }
        },
        [focus]
      );

      return (
        <SelectField<string[]>
          className={className}
          disabled={disabled}
          filterOption={defaultFilterOption}
          maxTagCount="responsive"
          mode="multiple"
          onChange={(selected: string[]) =>
            onChange?.({ ...value, value: fromSelectValues(selected) })
          }
          options={selectOptions}
          placeholder={
            placeholder ??
            intl.formatMessage({
              id: "filters.sourceObject.select.placeholder",
              defaultMessage: "Click or type to select source object",
              description: "Placeholder displayed in the Source Object filter's select field",
            })
          }
          ref={setRef}
          showSearch
          size="middle"
          value={Array.isArray(value.value) ? toSelectValues(value.value) : undefined}
        />
      );
    },
};

export default SourceObjectFilter;
